<template>
    <div class="marketing-site-gallery">
        <ds-icon-button class="go-to-list-button" name="arrow-left" @click="goToList" />
        <div class="header">
            <h2>{{ $options.TITLE }}</h2>
        </div>
        <ds-tabset
            id="tab-element"
        >
            <ds-tab
                :label="$options.GALLERY"
                name="gallery"
                selected
            >
                <section>
                    <marketing-site-templates
                        :get-templates="getGalleryTemplates"
                        :show-pagination="fs50916FlagEnabled"
                        :source="$options.GALLERY_SOURCE"
                        @click-create="createFromScratch"
                        @select-template="selectTemplate"
                        @preview-template="previewTemplate"
                    />
                </section>
            </ds-tab>
            <ds-tab
                :label="$options.MY_TEMPLATES"
                name="my-templates"
            >
                <section>
                    <marketing-site-templates
                        :get-templates="getSavedTemplates"
                        show-pagination
                        is-my-templates-tab
                        :source="$options.SAVED_SOURCE"
                        :copy-template="copyTemplateGroup"
                        :rename-template="renameTemplateGroup"
                        @click-create="createFromScratch"
                        @select-template="selectTemplate"
                        @preview-template="previewTemplate"
                        @edit="onEditTemplate"
                    />
                </section>
            </ds-tab>
            <ds-tab
                :label="$options.PUBLISHED"
                name="published"
            >
                <section>
                    <marketing-site-templates
                        :get-templates="getPublishedTemplates"
                        show-pagination
                        :source="$options.PUBLISHED_SOURCE"
                        @click-create="createFromScratch"
                        @select-template="selectTemplate"
                        @preview-template="previewTemplate"
                    />
                </section>
            </ds-tab>
        </ds-tabset>
        <choose-site-name-modal
            :is-open="chooseNameModalOptions.isOpen"
            :initial-name="chooseNameModalOptions.initialName"
            :title="chooseNameModalOptions.title"
            :helper-text="chooseNameModalOptions.helperText"
            :name-input-label="chooseNameModalOptions.nameInputLabel"
            :validate="chooseNameModalOptions.validate"
            @close="handleCancelChooseName"
            @rename="handleChooseName"
        />
    </div>
</template>

<script>
import axios from 'axios';
import ChooseSiteNameModal from './ChooseSiteNameModal';
import MarketingSiteTemplates from './MarketingSiteTemplates';
import {
    SITE_STATUS,
    TEMPLATE_MEDIA,
    EDITOR_ROUTE_URL,
} from '../util/marketing/unlayer.constants';
import {
    BLANK_DESIGN,
    cloneSiteFromSite,
    GALLERY_SOURCE,
    PUBLISHED_SOURCE,
    SAVED_SOURCE,
} from '../util/marketing/site.utils';
import {
    createTemplateGroup,
    getTemplateGroup,
    getGlobalTemplateGroup,
    createTemplateForTemplateGroup,
    patchTemplateGroup,
} from '../util/marketing/marketingSiteEditorApi';
import { convert as slugify } from 'url-slug';

export const TEMPLATE_GROUP_LIST = '/app/marketingsite/home';
export const TITLE = 'Select a template';
export const GALLERY = 'Gallery';
export const MY_TEMPLATES = 'My Templates';
export const PUBLISHED = 'Published';
export const RENAME_MODAL_TITLE = 'Name your landing page';
export const NAME_INPUT_LABEL = 'Landing page name';
export const HELPER_TEXT = 'This will appear in the browser title bar and landing page url.';
export const ERROR_COPYING_SITE = 'There was an error copying this site';
export const ERROR_CREATING_SITE = 'There was an error creating this site';
export const GALLERY_SOURCE_URL = '/app/marketingsite/list/gallery';
export const PUBLISHED_SOURCE_URL = '/app/marketingsite/list/published';
export const SAVED_SOURCE_URL = '/app/marketingsite/list/saved';
export const MARKETING_SITE_AUDIENCES = {
    BLUE: 'BLUE',
    RED: 'RED',
    GREEN: 'GREEN',
};

export default {
    TITLE,
    GALLERY,
    MY_TEMPLATES,
    PUBLISHED,
    GALLERY_SOURCE,
    PUBLISHED_SOURCE,
    SAVED_SOURCE,

    components: {
        ChooseSiteNameModal,
        MarketingSiteTemplates,
    },

    props: {
        tenantId: {
            type: String,
        },
        currentEnv: {
            type: String,
        },
    },

    data() {
        return {
            chooseNameModalOptions: {},
        };
    },

    computed: {
        environment() {
            return { production: 'production', integration: 'integration', staging: 'staging' }[this.currentEnv] || 'integration';
        },

        fs50916FlagEnabled() {
            return window.fs50916FlagEnabled === 'true';
        },

        greenFlagEnabled() {
            return window.greenFlagEnabled === 'true';
        },

        blueFlagEnabled() {
            return window.blueFlagEnabled === 'true';
        },

        redFlagEnabled() {
            return window.redFlagEnabled === 'true';
        },
    },

    methods: {
        async getGalleryTemplates(page, limit) {
            const myAudience = [];

            if (this.greenFlagEnabled) myAudience.push(MARKETING_SITE_AUDIENCES.GREEN);
            if (this.blueFlagEnabled) myAudience.push(MARKETING_SITE_AUDIENCES.BLUE);
            if (this.redFlagEnabled) myAudience.push(MARKETING_SITE_AUDIENCES.RED);

            const audience = myAudience.join(',') || null;
            const queryString = new URLSearchParams({ page, limit, audience });

            return axios.get(`${GALLERY_SOURCE_URL}?${queryString}`);
        },
        async getSavedTemplates(pageToken, pageSize) {
            const queryString = new URLSearchParams({ pageToken, pageSize });

            return axios.get(`${SAVED_SOURCE_URL}?${queryString}`);
        },
        async getPublishedTemplates(pageToken, pageSize) {
            const queryString = new URLSearchParams({ pageToken, pageSize });

            return axios.get(`${PUBLISHED_SOURCE_URL}?${queryString}`);
        },
        async createFromScratch() {
            const title = await this.chooseName(NAME_INPUT_LABEL);

            const newTemplateGroup = {
                title,
                alias: slugify(title.toLowerCase()),
                html: '<html><body></body></html>',
                builderJson: {
                    content: {},
                    options: {},
                },
                templateGroupStatus: SITE_STATUS.DRAFT,
                templates: [],
                templateMedia: TEMPLATE_MEDIA.Web,
            };

            try {
                const response = await createTemplateGroup(newTemplateGroup);

                const template = {
                    templateMedia: 'WEB',
                    templateSource: 'UNLAYER',
                    templateStatus: 'DRAFT',
                    title: 'Landing Page',
                    alias: 'landing-page',
                    templateGroupId: response.id,
                    groupOrder: 1,
                    builderJson: {
                        content: BLANK_DESIGN,
                        options: { chunks: {}, seoDescription: '' },
                    },
                    formList: [],
                    html: '<html></html>',
                };

                await createTemplateForTemplateGroup(response.id, template);
                this.editTemplateGroup(response.id);
            } catch (err) {
                this.$error({ message: ERROR_CREATING_SITE, bottom: true });
            } finally {
                this.chooseNameModalOptions = {};
                this.closeChooseNameModal();
            }

            return newTemplateGroup;
        },

        editTemplateGroup(id) {
            window.location.href = `${EDITOR_ROUTE_URL}/${id}`;
        },

        async selectTemplate({ sourceTemplateId, templateTitle, source }) {
            const name = await this.chooseName(templateTitle);
            const templateGroup = await this.loadSourceTemplate(source, sourceTemplateId);

            if (templateGroup) {
                try {
                    if (name) {
                        const clonedSite = await cloneSiteFromSite(templateGroup, name, this.tenantId, this.environment, { source });

                        this.editTemplateGroup(clonedSite.id);
                    }
                } catch (err) {
                    this.$error({ message: ERROR_COPYING_SITE, bottom: true });
                } finally {
                    this.chooseNameModalOptions = {};
                    this.closeChooseNameModal();
                }
            }
        },

        goToList() {
            window.location.href = TEMPLATE_GROUP_LIST;
        },

        previewTemplate({ url }) {
            window.open(url, '_blank');
        },

        async onEditTemplate(template) {
            const templateGroup = await this.loadSourceTemplate('', template.id);

            this.editTemplateGroup(templateGroup.id);
        },

        handleCancelChooseName() {
            if (this.chooseNameModalOptions?.resolve) {
                this.chooseNameModalOptions?.resolve?.call(this);
                this.selectedTemplate = {};
                this.chooseNameModalOptions = {};
            }
        },

        handleChooseName(name) {
            this.chooseNameModalOptions?.resolve?.call(this, name);
        },

        closeChooseNameModal() {
            this.chooseNameModalOptions = {};
        },


        async renameTemplateGroup(templateGroup) {
            const title = await this.chooseName(templateGroup?.title);

            if (!title) return null;

            await patchTemplateGroup(templateGroup.id, { title });

            this.closeChooseNameModal();

            return templateGroup;
        },


        async copyTemplateGroup(templateGroup, source) {
            const response = await getTemplateGroup(templateGroup.id);

            if (response?.data?.templateGroupDto) {
                const sourceTemplate = response.data.templateGroupDto;
                const { title: oldName } = sourceTemplate;
                const name = await this.chooseName(`Copy of ${ oldName }`);

                if (!name) { return null; }

                try {
                    await cloneSiteFromSite(sourceTemplate, name, this.tenantId, this.environment, { source, saveAsTemplate: true });

                    this.closeChooseNameModal();
                    this.selectedTemplate = {};
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error({ err });
                    this.$error({ message: ERROR_COPYING_SITE, bottom: true });
                } finally {
                    this.chooseNameModalOptions = {};
                }
            }

            return templateGroup;
        },

        chooseName(initialName, title = RENAME_MODAL_TITLE, nameInputLabel = NAME_INPUT_LABEL, helperText = HELPER_TEXT, validate = true) {
            this.chooseNameModalOptions?.resolve?.call();

            return new Promise((resolve) => {
                this.chooseNameModalOptions = {
                    isOpen: true,
                    initialName,
                    resolve,
                    title,
                    nameInputLabel,
                    helperText,
                    validate,
                };
            });
        },

        async loadSourceTemplate(source, templateId) {
            if (GALLERY_SOURCE === source) {
                const response = await getGlobalTemplateGroup(templateId);

                return response?.data;
            }
            const response = await getTemplateGroup(templateId);

            return response?.data?.templateGroupDto;
        },
    },
};
</script>
<style  lang="scss" scoped>
div.marketing-site-gallery {
    min-height: 100%;
    justify-content: space-between;
    width: calc(100vw - 3rem);  // released advance of fs.36592.make.max.classic.header.full.width -  need to reasess
    .go-to-list-button {
        position: absolute;
    }
    div.header h2 {
        margin: auto;
        font-weight: 400;
        font-size: 1.5rem;
    }
    div.header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1rem;
    }
    div.filter {
        display: flex;
        justify-content: space-between;
        padding-bottom: 0.75rem;
    }
    div.filter div.input-search {
        flex-direction: unset;
        width: 350px;
    }
}
</style>
<style>
    div.filter div.input-search input {
    height: 24px;
}
div.page-size div.limit-select{
    margin: auto;
}
.marketing-site-gallery #tab-element > .tab-header ul.tablist {
    border-bottom: none;
    display: grid;
    grid-template-columns: auto auto auto;
    grid-gap: 20px;
}
.marketing-site-gallery #tab-element > .tab-header ul.tablist > li {
    margin: auto;
    border-bottom-width: 2px;
    padding: 0px;
}
.marketing-site-gallery #tab-element > .tab-content {
    position: inherit;
    left: 0;
    top: 0;
    clear: both;
    visibility: visible;
}
#tab-element > div.tab-header > div > ul {
    margin: auto;
    width: fit-content;
}
</style>
